import React from 'react'
import { API_PATH_ADMIN_CUSTOMER_WORKBENCH } from '../../../../config/api'
import { getApiConfig, getLocalServerURL } from '../../../../utils/getApiConfig'
import { ProfileValues } from '../../../Profile/types'
import { onError } from '../../../../utils/onError'
import { AdminCustomerWorkbenchData } from '../types'

const EMPTY_WORKBENCH_DATA: AdminCustomerWorkbenchData = {
  profile: {
    ...ProfileValues,
    customerPaymentProfileIds: [],
    customerAddressIds: [],
  },
  paymentProfiles: [],
  shippingAddresses: [],
  transactions: [],
  subscriptions: [],
  rentalDetails: [],
}

export const useAdminCustomerWorkbench = ({
  customerProfileId,
  open,
}: {
  customerProfileId?: number
  open: boolean
}) => {
  const [data, setData] = React.useState<AdminCustomerWorkbenchData>(
    EMPTY_WORKBENCH_DATA
  )
  const [error, setError] = React.useState<string | null>(null)
  const [loading, setLoading] = React.useState(false)
  const [refreshNonce, setRefreshNonce] = React.useState(0)

  const refresh = React.useCallback(() => {
    setRefreshNonce((current) => current + 1)
  }, [])

  React.useEffect(() => {
    let active = true

    if (!open || customerProfileId == null || !Number.isFinite(customerProfileId)) {
      setData(EMPTY_WORKBENCH_DATA)
      setError(null)
      setLoading(false)
      return
    }

    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const config = await getApiConfig({
          body: { customerProfileId },
        })

        if (config == null) {
          throw new Error('Could not authorize admin request')
        }

        const response = await fetch(
          getLocalServerURL(API_PATH_ADMIN_CUSTOMER_WORKBENCH),
          config
        )

        if (!response.ok) {
          throw new Error('Failed to load customer workbench')
        }

        const nextData = (await response.json()) as Partial<AdminCustomerWorkbenchData>

        if (!active) {
          return
        }

        setData({
          profile: {
            ...ProfileValues,
            ...(nextData.profile ?? {}),
            customerPaymentProfileIds:
              nextData.profile?.customerPaymentProfileIds ?? [],
            customerAddressIds: nextData.profile?.customerAddressIds ?? [],
          },
          paymentProfiles: nextData.paymentProfiles ?? [],
          shippingAddresses: nextData.shippingAddresses ?? [],
          transactions: nextData.transactions ?? [],
          subscriptions: nextData.subscriptions ?? [],
          rentalDetails: nextData.rentalDetails ?? [],
        })
      } catch (loadError) {
        if (!active) {
          return
        }
        const message =
          loadError instanceof Error
            ? loadError.message
            : 'Failed to load customer workbench'
        setData(EMPTY_WORKBENCH_DATA)
        setError(message)
        onError(message)
      } finally {
        if (active) {
          setLoading(false)
        }
      }
    }

    void load()

    return () => {
      active = false
    }
  }, [customerProfileId, open, refreshNonce])

  return {
    ...data,
    error,
    loading,
    refresh,
  }
}
